import React from "react"
import ProjectBasic from "./basic"
import ProjectCalendar from "./calendar"
import ProjectGantt from "./gantt"
import ProjectKanban from "./kanban"
import ProjectTasks from "./tasks"
import ProjectTodos from "./todos"
import ProjectDashboard from "./dashboard"
import ProjectReportedHours from "./reported-hours"
import ProjectNavbar from "../../../projects/components/Navbar/ProjectNavbar"
import ErrorList from "../../../components/ErrorList"
import ProjectArchivedAlert from "../../../projects/components/ProjectArchivedAlert"
import { Route, RouteComponentProps, Switch } from "react-router-dom"
import { CircularProgress, Grid } from "@material-ui/core"
import { useProject } from "../../../projects/hooks/use-project"
import { paths } from "../../../paths"
import { useFullscreen } from "ahooks"
import { APP_MAX_WIDTH, COLOR_LIGHT } from "../../../constants"
import { Alert } from "@material-ui/lab"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { ProjectRouteContextProvider } from "../hooks/use-project-route-context"
import { ProjectCalendarSettingsProvider } from "../../providers/project-calendar-settings"
import { useTranslation } from "react-i18next"

type RouteParams = { projectId: string }

const ProjectLayout = ({ match, location }: RouteComponentProps<RouteParams>) => {
  const translations = useTranslations()
  const projectId = match.params.projectId

  return (
    <ProjectCalendarSettingsProvider
      projectId={projectId}
      loadingFallback={<CircularProgress />}
      errorFallback={<ErrorList errors={[translations.fetchErrorCalendarSettings]} />}
    >
      <ProjectLayoutContent match={match} location={location} />
    </ProjectCalendarSettingsProvider>
  )
}

const ProjectLayoutContent = ({ match, location }: Pick<RouteComponentProps<RouteParams>, "match" | "location">) => {
  const isSmallScreen = useIsSmallScreen()
  const translations = useTranslations()
  const projectId = match.params.projectId
  const { project, isLoading: isLoadingProject, error: projectError } = useProject(projectId)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [isBaselineBarShown, setIsBaselineBarShown] = React.useState(false)
  const [isActualBarShown, setIsActualBarShown] = React.useState(true)
  const [isFullScreen, { toggleFullscreen }] = useFullscreen(containerRef)

  const toggleActualBar = () => setIsActualBarShown((prev) => !prev)
  const toggleBaselineBar = () => setIsBaselineBarShown((prev) => !prev)

  const isLoading = isLoadingProject
  if (isLoading) return <CircularProgress />
  if (projectError) return <ErrorList errors={[translations.fetchErrorProject]} />
  if (!project) return <ErrorList errors={[translations.projectNotFoundError]} />
  const showFullScreen =
    !location.pathname.includes("basic") &&
    !location.pathname.includes("dashboard") &&
    !location.pathname.includes("reported-hours")

  return (
    <ProjectRouteContextProvider
      value={{
        isActualBarShown,
        isBaselineBarShown,
        isFullScreen,
        toggleActualBar,
        toggleBaselineBar,
        toggleFullscreen,
      }}
    >
      <div ref={containerRef} style={{ background: COLOR_LIGHT }}>
        <ProjectNavbar project={project} isFullWidth={showFullScreen} />
        {project.isTemplate && showFullScreen && (
          <div style={{ margin: "10px 0px" }}>
            <Alert severity="info">{translations.projectTemplateInfo}</Alert>
          </div>
        )}
        {project.isTemplate && !showFullScreen && (
          <Grid container alignItems="center" direction="column">
            <Grid item style={{ width: APP_MAX_WIDTH, margin: "10px 0px" }}>
              <Alert severity="info">{translations.projectTemplateInfo}</Alert>
            </Grid>
          </Grid>
        )}
        <ProjectArchivedAlert project={project} />
        <Switch>
          <Route path={paths.projectBasic()} component={ProjectBasic} />
          <Route path={paths.projectCalendar()} component={ProjectCalendar} />
          {isSmallScreen ? null : <Route path={paths.projectGantt()} component={ProjectGantt} />}
          <Route path={paths.projectKanban()} component={ProjectKanban} />
          <Route path={paths.projectTasks()} component={ProjectTasks} />
          <Route path={paths.projectTodos()} component={ProjectTodos} />
          <Route path={paths.projectReportedHours()} component={ProjectReportedHours} />
          <Route path={paths.projectDashboard()} component={ProjectDashboard} />
        </Switch>
      </div>
    </ProjectRouteContextProvider>
  )
}

const defaultTranslations = {
  projectNotFoundError: "Project not found",
  fetchErrorProject: "Failed to fetch project",
  projectTemplateInfo: "You are inside a project template",
  fetchErrorCalendarSettings: "Failed to fetch calendar settings",
}

const useTranslations = (): typeof defaultTranslations => {
  const { t } = useTranslation("translation")
  const { t: projectT } = useTranslation("project")

  return {
    projectNotFoundError: t("projectNotFoundError", defaultTranslations.projectNotFoundError),
    fetchErrorProject: t("fetchErrorProject", defaultTranslations.fetchErrorProject),
    projectTemplateInfo: projectT("projectTemplateInfo", defaultTranslations.projectTemplateInfo),
    fetchErrorCalendarSettings: t("fetchErrorCalendarSettings", defaultTranslations.fetchErrorCalendarSettings),
  }
}

export default ProjectLayout
